import React, { useCallback, useEffect, useState } from 'react';
import { Card, List, Button, Space, Tag, Segmented, Badge, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import notificationService, { emitUnread } from '../../services/notificationService';
import { useTranslation } from '../../../shared/i18n';
import { formatDateTime } from '../../../shared/utils/date';
import { userPath } from '../../../shared/config/paths';

const { Text, Paragraph } = Typography;
export const LEVEL_COLOR = { critical: 'red', warning: 'orange', info: 'blue' };

/** 打开通知链接：控制台相对路径走路由，外链新窗口 */
export const openLink = (navigate, link) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) window.open(link, '_blank', 'noopener');
    else navigate(userPath(link));
};

/** 站内信列表（保留 90 天） */
export default function NotificationInbox() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, unread: 0 });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await notificationService.inbox({ page, page_size: 20, unread_only: filter === 'unread' ? 1 : 0 });
            setData(d);
            emitUnread(d.unread);
        } catch (e) {
            setData({ items: [], total: 0, unread: 0 });
        } finally {
            setLoading(false);
        }
    }, [page, filter]);

    useEffect(() => { load(); }, [load]);

    const read = async (ids) => {
        try {
            const r = await notificationService.markRead(ids);
            emitUnread(r.unread);
            load();
        } catch (e) {
            message.error(e.error || t('Operation failed'));
        }
    };

    const click = (item) => {
        if (!item.read_at) read([item.id]);
        openLink(navigate, item.link);
    };

    return (
        <Card
            size="small"
            title={(
                <Segmented
                    size="small"
                    value={filter}
                    onChange={(v) => { setFilter(v); setPage(1); }}
                    options={[{ label: t('All'), value: 'all' }, { label: <Badge count={data.unread} size="small" offset={[8, -2]}>{t('Unread')}</Badge>, value: 'unread' }]}
                />
            )}
            extra={<Button size="small" disabled={!data.unread} onClick={() => read(null)}>{t('Mark all read')}</Button>}
        >
            <List
                loading={loading}
                dataSource={data.items}
                locale={{ emptyText: t('No messages') }}
                pagination={data.total > 20 ? { current: page, pageSize: 20, total: data.total, onChange: setPage, size: 'small', showSizeChanger: false } : false}
                renderItem={(item) => (
                    <List.Item style={{ cursor: item.link || !item.read_at ? 'pointer' : 'default', opacity: item.read_at ? 0.65 : 1 }} onClick={() => click(item)}>
                        <List.Item.Meta
                            avatar={<Badge dot={!item.read_at} color={LEVEL_COLOR[item.level]} />}
                            title={(
                                <Space size={6} wrap>
                                    <Text strong={!item.read_at}>{item.title}</Text>
                                    <Tag bordered={false} color={item.level === 'info' ? 'default' : LEVEL_COLOR[item.level]}>{t(item.category_label)}</Tag>
                                </Space>
                            )}
                            description={(
                                <>
                                    {item.body && <Paragraph type="secondary" style={{ marginBottom: 2, whiteSpace: 'pre-line' }} ellipsis={{ rows: 2 }}>{item.body}</Paragraph>}
                                    <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(item.created_at)}</Text>
                                </>
                            )}
                        />
                    </List.Item>
                )}
            />
        </Card>
    );
}
